// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setLabelOrderUpdateInterval, toggleAutomaticLabelOrderUpdate } from 'store/reducers/projectLabelSlice';
import { Dialog, FooterButton, InputFieldValue, Switch } from '@tls/treact-ui';

export type Props = {
  open: boolean;
  onClose: () => void;
};

export default function LabelOrderSettingsDialog({ open, onClose }: Readonly<Props>) {
  const { t } = useTranslation();

  const automate = useAppSelector(s => s.label.automaticLabelOrderUpdate);
  const interval = useAppSelector(s => s.label.labelOrderUpdateInterval);

  const dispatch = useAppDispatch();
  const toggleAutomate = useCallback(() => dispatch(toggleAutomaticLabelOrderUpdate()), [dispatch]);
  const setInterval = useCallback((e: number | null) => dispatch(setLabelOrderUpdateInterval(e)), [dispatch]);
  return (
    <Dialog open={open} enableClosing onClose={onClose} data-testid='label-images-order-settings-dialog'>
      <Dialog.DialogHeader label={t('project.label.images.order.settings')} />
      <Dialog.DialogContent>
        <Switch
          value={automate}
          label={t('project.label.images.order.automate')}
          onChange={toggleAutomate}
          data-testid='label-images-order-settings-automate-switch'
        />
        <InputFieldValue
          label={t('project.label.images.order.refresh')}
          suffix={t('project.label.images.order.seconds')}
          onValueChange={setInterval}
          value={interval}
          disabled={!automate}
          step={1}
          min={5}
          max={90}
          data-testid='label-images-order-settings-refresh-input'
        />
      </Dialog.DialogContent>
      <Dialog.Footer
        right={[
          <FooterButton
            key='label-settings-cancel'
            label={t('close')}
            variant='secondary'
            onClick={onClose}
            data-testid='label-images-order-settings-close-button'
          />,
        ]}
      />
    </Dialog>
  );
}
